import { useCallback } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Box, Divider, Grid2 as Grid, IconButton, Paper, Stack, TextField, Tooltip } from "@mui/material";

import FormattedNumericField from "../../../../../../Components/NumericTextField/FormattedNumericField";
import {
  type Product,
  PRODUCT_MULTIPLIERS,
  PRODUCT_UNITS,
  type ProductMultipliers,
  type ProductOption,
  type ProductUnit,
} from "../../types";

type Props = {
  productIndex: number;
  product: Product;
  productOptions: ProductOption[];
  onChange: (productIndex: number, updatedProduct: Product) => void;
  onDelete: (productIndex: number) => void;
  deleteDisabled: boolean;
};

export default function Product({ product, productOptions, onChange, productIndex, onDelete, deleteDisabled }: Props) {
  const handleChange = useCallback(
    <T extends "quantity" | "price" | "unit" | "multiplier">(name: T) =>
      (
        value: T extends "quantity" | "price"
          ? number | null
          : T extends "unit"
            ? ProductUnit | null
            : ProductMultipliers | null
      ) => {
        const updatedProduct = {
          ...product,
          [name]: value,
          errors: {},
        };
        onChange(productIndex, updatedProduct);
      },
    [product, onChange, productIndex]
  );

  const handleChangeType = useCallback(
    (_, selectedProduct: ProductOption) => {
      const updatedProduct: Product = {
        ...selectedProduct,
        uniqueId: product.uniqueId,
        quantity: null,
        multiplier: null,
        price: null,
        unit: null,
        errors: {},
      };
      onChange(productIndex, updatedProduct);
    },
    [onChange, product.uniqueId, productIndex]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Grid container spacing={1} flexGrow={1}>
          <Grid size={12}>
            <Autocomplete
              id="product-type"
              data-cy="product-type"
              blurOnSelect
              disableClearable
              options={productOptions}
              value={
                productOptions.find((option) => option.label === product.label) ?? {
                  id: "",
                  label: "",
                  usageType: "",
                }
              }
              onChange={handleChangeType}
              isOptionEqualToValue={(o, v) => o.label === v.label}
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Product type"
                  fullWidth
                  error={!!product.errors.label}
                  helperText={product.errors.label}
                />
              )}
            />
          </Grid>
          <Grid container size={12}>
            <Grid flex={1}>
              <FormattedNumericField
                label="Quantity"
                value={product.quantity}
                required
                onChange={handleChange("quantity")}
                error={!!product.errors.quantity}
                helperText={product.errors.quantity}
              />
            </Grid>
            <Grid flex={1}>
              <Autocomplete
                id="multiplier"
                options={Object.values(PRODUCT_MULTIPLIERS)}
                value={product.multiplier}
                onChange={(_, value) => {
                  handleChange("multiplier")(value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Multiplier"
                    error={!!product.errors.multiplier}
                    helperText={product.errors.multiplier}
                    required
                  />
                )}
              />
            </Grid>
            <Divider orientation="vertical" flexItem sx={{ height: "100%", mx: 1 }} />
            <Grid flex={1}>
              <FormattedNumericField
                label="Price per unit"
                value={product.price}
                onChange={handleChange("price")}
                required
                error={!!product.errors.price}
                prefix="$"
              />
            </Grid>
            <Grid flex={1}>
              <Autocomplete
                id="unit"
                options={Object.values(PRODUCT_UNITS)}
                value={product.unit}
                onChange={(_, value) => {
                  handleChange("unit")(value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Unit"
                    error={!!product.errors.unit}
                    helperText={product.errors.unit}
                    required
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
        <Box>
          <Tooltip title={deleteDisabled ? "Subscription should have at least one product" : ""}>
            <Box sx={{ cursor: "pointer" }}>
              <IconButton
                data-cy="closeButton"
                aria-label="close"
                disabled={deleteDisabled}
                onClick={() => {
                  onDelete(productIndex);
                }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Stack>
    </Paper>
  );
}
